import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
//Preference
import Preferences from 'Core/containers/Preferences';

//Components
import Header from 'Core/containers/Header';
import Sidebar from 'Core/containers/Sidebar';
import AuthContainer from 'Core/containers/Auth';
import GeneralContainer from 'Core/containers/General';
import Catalog from '../core/catalogs'
import { Loading } from '@gpstrackit/react-controls';
import { ValidateVersion } from 'Core/security';
import ErrorView from "Core/views/error";

//Views
import SendCommand from 'Modules/devices/.components/sendCommand';

//Helpers
import { copyToClipboard } from 'Core/data/Helpers';

//Notification
import { NotificationManager } from "Components/Notifications";

//Actions
import { clearRedux, toggleSidebar, sendCommandOpenModal } from "Redux/actions";

//Styles
import './modules.scss';

const lazyWithRetry = (componentImport) =>
    lazy(async () => {
        const pageHasAlreadyBeenForceRefreshed = JSON.parse(
            window.localStorage.getItem(
                'page-has-been-force-refreshed'
            ) || 'false'
        );

        try {
            const component = await componentImport();

            window.localStorage.setItem(
                'page-has-been-force-refreshed',
                'false'
            );

            return component;
        } catch (error) {
            if (!pageHasAlreadyBeenForceRefreshed) {
                // Assuming that the user is not on the latest version of the application.
                // Let's refresh the page immediately.
                window.localStorage.setItem(
                    'page-has-been-force-refreshed',
                    'true'
                );
                return window.location.reload();
            }

            // The page has already been reloaded
            // Assuming that user is already using the latest version of the application.
            // Let's let the application crash and raise the error.
            throw error;
        }
    });


//Views
const AccountsView = lazyWithRetry(() => import('Modules/accounts'));
const DealersView = lazyWithRetry(() => import('Modules/dealers'));
const DevicesView = lazyWithRetry(() => import('Modules/devices'));
const VidfleetView = lazyWithRetry(() => import('Modules/vidfleet'));
const KeyFobsView = lazyWithRetry(() => import('Modules/keysFobs'));
const UserActivityLogsView = lazyWithRetry(() => import('Modules/activities'));
const PMUsers = lazyWithRetry(() => import('Modules/pmUsers'));
const PackagesView = lazyWithRetry(() => import('Modules/packages'));
const TransactionsView = lazyWithRetry(() => import('Modules/transactions'));
const ManufacturersView = lazyWithRetry(() => import('Modules/manufacturers'));
const LoginPageView = lazyWithRetry(() => import('Modules/loginPage'));
const ReportsView = lazyWithRetry(() => import('Modules/reports'));
const ImportP4View = lazyWithRetry(() => import('Modules/importP4'));
const ImportAccountView = lazyWithRetry(() => import('Modules/importAccount'));
const DashboardView = lazyWithRetry(() => import('Modules/dashboard'));
const AlertTypesView = lazyWithRetry(() => import('Modules/alertTypes'));
const ScheduledActionsView = lazyWithRetry(() => import('Modules/scheduledActions'));
const EventsView = lazyWithRetry(() => import('Modules/events'));
const UnsubscribedContactsView = lazyWithRetry(() => import('Modules/unsubscribedContacts'));
const SimProvidersView = lazyWithRetry(() => import('Modules/simProviders'));
const SimProviderAccountsView = lazyWithRetry(() => import('Modules/simProviderAccounts'));
const SmsSystemsView = lazyWithRetry(() => import('Modules/smsSystems'));
const MaintenanceWarningEvents = lazyWithRetry(() => import('Modules/maintenanceWarningEvents'));
const PMTags = lazyWithRetry(() => import('Modules/tags'));
const BrandsView = lazyWithRetry(() => import('Modules/brands'));
const ModulesRoutes = (props) => {
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const exportResult = useSelector(state => state.generalRedux.exportResult);
    const exportRequest = useSelector(state => state.generalRedux.export);
    const createNotificationId = useSelector(state => state.generalRedux.createNotificationId);
    const sendCommandsOpenModal = useSelector(state => state.sendCommandRedux.openModal);
    const massSMS = useSelector(state => state.sendCommandRedux.massSMS);
    const navigate = useNavigate();
    const preferencesRef = useRef(null);

    let location = useLocation();

    const changePreferences = (newValues) => {
        const oldPreferences = preferencesRef?.current?.getPreferences("Other", "drawer") || {}
        if (!_.isEqual(newValues, oldPreferences)) {
            const currentPreferences = { ...oldPreferences, show: newValues }
            preferencesRef.current.savePreference("Other", "drawer", currentPreferences)
        }
    }

    useEffect(() => {
        if (exportResult) {
            NotificationManager.success(
                messages['export_message'],
                messages['export'],
                3000
            );
            dispatch(clearRedux("GENERAL", { exportResult: null }))
        }
        if (exportRequest) {
            NotificationManager.success(
                messages['menu_account_notifications_file_generated'],
                messages['export'],
                6000
            );
            dispatch(clearRedux("GENERAL", { export: null }))
        }
        if (createNotificationId) {
            let message = messages['mass_sms_request_has_ended']
            NotificationManager.success(message, messages['send_commands_title'], 10000, () => {
                copyToClipboard(createNotificationId, messages['common_copied']);
                navigate(`/app/transactions/list/${createNotificationId}`);

            }, null, '', null);
            dispatch(clearRedux("GENERAL", { createNotificationId: null }))
        }
    }, [exportResult, exportRequest, createNotificationId])

    const routers = ['/app/devices/list', '/app/pmusers/list']

    return (
        <AuthContainer>
            <Preferences
                ref={preferencesRef}
                preferencesKey="drawer"
                onInitialPreferences={(preferences) => {
                    if (preferences?.show != undefined && preferences.show != null) {
                        let value = preferences?.show
                        dispatch(toggleSidebar(value))
                    }
                }}
            >
                {ValidateVersion("head")}
                <GeneralContainer />
                <Catalog />
                <div style={{ position: 'relative', zIndex: 9999 }}>
                    <Header changePreferences={changePreferences} />
                    <div id='id_appContainer' className='app-container'>
                        <Sidebar changePreferences={changePreferences} />
                        <div className="module-container" style={{ 
                            overflowY: routers.includes(location?.pathname) || 
                            (
                                location?.pathname?.includes('/app/accounts/overview/') || 
                                location?.pathname?.includes('/app/accounts/list') ||
                                location?.pathname?.includes('/app/dealers/overview')
                            )
                                ? 'auto' 
                                : '' 
                        }}
                        >
                            <Suspense fallback={<Loading overlay show />}>
                                <Routes>
                                    <Route path={`${props.to}/accounts/*`} element={<AccountsView />} />
                                    <Route path={`${props.to}/dealers/*`} element={<DealersView />} />
                                    <Route path={`${props.to}/useractivitylogs/*`} element={<UserActivityLogsView />} />
                                    <Route path={`${props.to}/devices/*`} element={<DevicesView />} />
                                    <Route path={`${props.to}/vidfleet/*`} element={<VidfleetView />} />
                                    <Route path={`${props.to}/keyfobs/*`} element={<KeyFobsView />} />
                                    <Route path={`${props.to}/pmusers/*`} element={<PMUsers />} />
                                    <Route path={`${props.to}/packages/*`} element={<PackagesView />} />
                                    <Route path={`${props.to}/transactions/*`} element={<TransactionsView />} />
                                    <Route path={`${props.to}/manufacturers/*`} element={<ManufacturersView />} />
                                    <Route path={`${props.to}/cloudloginpage`} element={<LoginPageView />} />
                                    <Route path={`${props.to}/reports/*`} element={<ReportsView />} />
                                    <Route path={`${props.to}/importp4/*`} element={<ImportP4View />} />
                                    <Route path={`${props.to}/importaccount/*`} element={<ImportAccountView />} />
                                    <Route path={`${props.to}/dashboard/*`} element={<DashboardView />} />
                                    <Route path={`${props.to}/alerttypes/*`} element={<AlertTypesView />} />
                                    <Route path={`${props.to}/scheduledactions/*`} element={<ScheduledActionsView />} />
                                    <Route path={`${props.to}/events/*`} element={<EventsView />} />
                                    <Route path={`${props.to}/unsubscribedcontacts/*`} element={<UnsubscribedContactsView />} />
                                    <Route path={`${props.to}/simproviders/*`} element={<SimProvidersView />} />
                                    <Route path={`${props.to}/simprovideraccounts/*`} element={<SimProviderAccountsView />} />
                                    <Route path={`${props.to}/smssystems/*`} element={<SmsSystemsView />} />
                                    <Route path={`${props.to}/maintenancewarningevents/*`} element={<MaintenanceWarningEvents />} />
                                    <Route path={`${props.to}/tags/*`} element={<PMTags />} />
                                    <Route path={`${props.to}/brands/*`} element={<BrandsView />} />
                                    <Route path="/error" element={<ErrorView />} />
                                </Routes>
                            </Suspense>
                        </div>
                    </div>
                </div>
                {sendCommandsOpenModal && massSMS && <SendCommand massSMS={true} />}
            </Preferences>
        </AuthContainer>
    )
}
export default ModulesRoutes;